import { useCallback, useEffect, useMemo, useState } from 'react';
import createContext from 'constate';
import { useTableMapAvailabilityValue } from './table-map/TableMapAvailabilityContext';

export enum BillDisplayMode {
  MAP = 'map',
  LIST = 'list',
}

const storageKey = 'openBill.displayMode';

const getModeFromStorage = (): BillDisplayMode => {
  const mode = localStorage.getItem(storageKey);
  return mode ? (mode as BillDisplayMode) : BillDisplayMode.LIST;
};

type Props = {
  locationId: number;
};

const [DigitalBillDisplayModeContextProvider, useDigitalBillDisplayMode] = createContext(({ locationId }: Props) => {
  const { isTableMapEnabled, isLoadingAvailability } = useTableMapAvailabilityValue({ locationId });
  const [billDisplayMode, setBillDisplayMode] = useState<BillDisplayMode>(() => getModeFromStorage());

  const setMode = useCallback((mode: BillDisplayMode) => {
    localStorage.setItem(storageKey, mode);
    setBillDisplayMode(mode);
  }, []);

  useEffect(() => {
    if (isLoadingAvailability) {
      return;
    }

    if (!isTableMapEnabled) {
      setMode(BillDisplayMode.LIST);
      return;
    }
  }, [billDisplayMode, isLoadingAvailability, isTableMapEnabled, setMode]);

  const contextValue = useMemo(
    () => ({
      billDisplayMode,
      setBillDisplayMode: setMode,
      isLoading: isLoadingAvailability,
    }),
    [billDisplayMode, isLoadingAvailability, setMode]
  );

  return contextValue;
});

export { DigitalBillDisplayModeContextProvider, useDigitalBillDisplayMode };
